export interface CloudflareImageTransform {
  /** Target width in pixels */
  width?: number;
  /** Target height in pixels */
  height?: number;
  /**
   * How the image is resized to fit the box.
   * - `cover`: fill the box, cropping if necessary (default for icons)
   * - `contain`: fit the whole image inside the box, may letterbox
   * - `scale-down`: like `contain` but never upscale
   * - `crop`: same as `cover` but anchored
   * - `pad`: like `contain` but pads to exact size
   */
  fit?: 'cover' | 'contain' | 'scale-down' | 'crop' | 'pad';
  /** JPEG/WebP/AVIF quality, 1-100 */
  quality?: number;
  /** Device pixel ratio multiplier */
  dpr?: number;
  /** Output format; `auto` lets Cloudflare pick AVIF/WebP based on Accept header */
  format?: 'auto' | 'webp' | 'avif' | 'json';
}

/**
 * Preset sizes for vendor icons. Source dimensions are 2x the rendered
 * logical size to stay sharp on retina displays.
 * - `sm` (96×96) — list rows, inline references
 * - `md` (192×192) — cards, modals, admin thumbnails
 * - `lg` (384×384) — checkout flow, hero treatments
 */
export type CloudflareImagePreset = 'sm' | 'md' | 'lg';

export const cloudflareImagePresets: Record<
  CloudflareImagePreset,
  CloudflareImageTransform
> = {
  sm: { width: 96, height: 96, fit: 'cover', format: 'auto' },
  md: { width: 192, height: 192, fit: 'cover', format: 'auto' },
  lg: { width: 384, height: 384, fit: 'cover', format: 'auto' },
};

/**
 * Build a Cloudflare Images delivery URL using flexible variants.
 *
 * Requires "flexible variants" to be enabled on the Images product. Pass
 * either a preset name (`sm` / `md` / `lg`) or a custom transform object.
 *
 * @example
 * buildCloudflareImageUrl(hash, id, 'md')
 * buildCloudflareImageUrl(hash, id, { width: 128, height: 128, fit: 'cover' })
 */
export function buildCloudflareImageUrl(
  accountHash: string,
  imageId: string,
  preset: CloudflareImagePreset | CloudflareImageTransform = 'md',
): string {
  const transform =
    typeof preset === 'string' ? cloudflareImagePresets[preset] : preset;

  const parts: string[] = [];
  if (transform.width !== undefined) parts.push(`w=${transform.width}`);
  if (transform.height !== undefined) parts.push(`h=${transform.height}`);
  if (transform.fit) parts.push(`fit=${transform.fit}`);
  if (transform.quality !== undefined) parts.push(`quality=${transform.quality}`);
  if (transform.dpr !== undefined) parts.push(`dpr=${transform.dpr}`);
  if (transform.format) parts.push(`format=${transform.format}`);

  const base = `https://imagedelivery.net/${accountHash}/${imageId}`;
  if (parts.length === 0) return `${base}/public`;
  return `${base}/${parts.join(',')}`;
}
